/**
 * 주정산 날짜 유틸리티
 * 매주 금요일 기준으로 수요일~화요일 정산 기간 계산
 */

/**
 * 날짜 포맷팅 (yyyy-MM-dd)
 */
export const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * 날짜 포맷팅 (M월 D일 금요일)
 */
export const formatFridayLabel = (date: Date): string => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}월 ${day}일 금요일`;
};

/**
 * 특정 금요일에 대한 정산 기간 계산 (전주 수요일 ~ 이번주 화요일)
 * @param friday 금요일 날짜
 * @returns 정산 시작일(전주 수요일)과 종료일(이번주 화요일)
 * 
 * 예시: 1월 16일 금요일 선택 시
 * - 시작일: 1월 7일 수요일 (금요일 - 9일)
 * - 종료일: 1월 13일 화요일 (금요일 - 3일)
 */
export const getSettlementPeriodForFriday = (friday: Date): { startDate: Date; endDate: Date } => {
    // 전주 수요일 (금요일 기준 9일 전)
    const startDate = new Date(friday);
    startDate.setDate(friday.getDate() - 9);
    
    // 이번주 화요일 (금요일 기준 3일 전)
    const endDate = new Date(friday);
    endDate.setDate(friday.getDate() - 3);
    
    return { startDate, endDate };
};

/**
 * 현재 달의 모든 금요일 목록 생성
 * @returns 금요일 날짜 배열 (오름차순 - 첫째 주부터)
 */
export const getCurrentMonthFridays = (): Date[] => {
    const fridays: Date[] = [];
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    // 이번 달의 첫날과 마지막 날
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // 첫 번째 금요일 찾기
    const firstDayOfWeek = firstDay.getDay();
    let firstFridayDate = 1;
    
    if (firstDayOfWeek <= 5) {
        // 월~금요일에 시작하면
        firstFridayDate = 1 + (5 - firstDayOfWeek);
    } else {
        // 토~일요일에 시작하면 다음 주 금요일
        firstFridayDate = 1 + (12 - firstDayOfWeek);
    }
    
    // 이번 달의 모든 금요일 추가 (오름차순)
    for (let date = firstFridayDate; date <= lastDay.getDate(); date += 7) {
        fridays.push(new Date(year, month, date));
    }
    
    return fridays;
};

/**
 * 금요일 옵션 생성
 */
export interface FridayOption {
    value: string; // yyyy-MM-dd 형식의 금요일 날짜
    label: string; // "M월 D일 금요일" 형식
    settlementPeriod: string; // "M월 D일 ~ M월 D일" 형식
}

export const generateFridayOptions = (): FridayOption[] => {
    const fridays = getCurrentMonthFridays();
    
    return fridays.map(friday => {
        const { startDate, endDate } = getSettlementPeriodForFriday(friday);
        
        return {
            value: formatDate(friday),
            label: formatFridayLabel(friday),
            settlementPeriod: `${startDate.getMonth() + 1}월 ${startDate.getDate()}일 ~ ${endDate.getMonth() + 1}월 ${endDate.getDate()}일`
        };
    });
};
